/**
 * Frühere E-Mails der Akte als Anhang (Kundenfeedback Kanzlei Polz
 * 24.10.2026, IV.11): „Beim Versenden von Erinnerungen per Mail (bspw. an die
 * HV) wäre es auch gut, wenn man auch Mails anhängen könnte und nicht nur
 * Dokumente."
 *
 * Die gewählte E-Mail wird als PDF (Von/An/Datum/Betreff + Inhalt, wie
 * „Als Anhang weiterleiten") beigefügt – das kann jeder Empfänger öffnen,
 * anders als eine .eml-Datei, die KARS für selbst versendete Nachrichten
 * nicht vorhält.
 *
 * Reine Funktionen für Liste und Beschriftung; Laden und Export übernimmt
 * der Dialog (CaseAttachmentPickerDialog).
 */
import type { ChatConversation, ChatMessage, Viewer } from '../chat/chatModel'
import { conversationActivity, isOwnMessage, isOwnSideParticipant, senderName, stripHtml } from '../chat/chatModel'
import { participantDisplayName } from '../participantDisplay'

const SNIPPET_LENGTH = 110

export interface MailEntry {
  id: number
  date: string
  outgoing: boolean
  sender: string
  snippet: string
  attachmentCount: number
}

/** Unterhaltungen mit der jüngsten Aktivität zuerst (unveränderte Eingabe). */
export function sortConversationsByActivity (list: readonly ChatConversation[]): ChatConversation[] {
  // eslint-disable-next-line unicorn/no-array-sort -- Kopie; toSorted fehlt in der TS-Zielbibliothek
  return [...list].sort((a, b) => conversationActivity(b).localeCompare(conversationActivity(a)))
}

/** Gegenseite einer Unterhaltung als kurzer Text („HUK-COBURG, Max Mustermann"). */
export function conversationCounterparts (conv: ChatConversation, viewer: Viewer): string {
  const names = (conv.participants || [])
    .filter(p => !isOwnSideParticipant(p, viewer))
    .map(p => participantDisplayName(p))
    .filter(Boolean)
  return [...new Set(names)].join(', ')
}

/** Eine Nachricht als Listeneintrag (neueste zuerst sortiert der Aufrufer). */
export function mailEntry (msg: ChatMessage, viewer: Viewer): MailEntry {
  const text = stripHtml(msg.content)
  return {
    id: msg.id,
    date: msg.createdAt,
    outgoing: isOwnMessage(msg, viewer),
    sender: senderName(msg),
    snippet: text.length > SNIPPET_LENGTH ? `${text.slice(0, SNIPPET_LENGTH).trimEnd()}…` : text,
    attachmentCount: msg.attachments?.length || 0,
  }
}

/** Nachrichten einer Unterhaltung als Einträge, neueste zuerst. */
export function mailEntries (messages: readonly ChatMessage[], viewer: Viewer): MailEntry[] {
  return [...messages]
    // eslint-disable-next-line unicorn/no-array-sort -- Kopie; toSorted fehlt in der TS-Zielbibliothek
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
    .map(m => mailEntry(m, viewer))
}

/** Datum/Uhrzeit für die Liste: „24.10.2026, 09:15". */
export function formatMailDate (iso: string | null | undefined): string {
  if (!iso) {
    return ''
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return ''
  }
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/**
 * Eindeutige Dateinamen für mehrere PDF-Anhänge: gleichnamige Exporte
 * erhalten „(2)", „(3)" … vor der Endung.
 */
export function uniqueFileNames (names: readonly string[]): string[] {
  const seen = new Map<string, number>()
  return names.map(name => {
    const count = (seen.get(name) || 0) + 1
    seen.set(name, count)
    if (count === 1) {
      return name
    }
    const dot = name.lastIndexOf('.')
    return dot > 0 ? `${name.slice(0, dot)} (${count})${name.slice(dot)}` : `${name} (${count})`
  })
}
